import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LOGO_URL } from '@/lib/brand';

const Privacy = () => {
    const lastUpdated = 'December 19, 2025';

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-10 h-10 object-contain" />
                        <span className="text-xl font-bold gradient-text">SHIRO</span>
                        <span className="text-xl font-light text-white/80">Security</span>
                    </Link>
                    <Link to="/">
                        <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Home
                        </Button>
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-32 pb-20 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-12">
                        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
                        <p className="text-white/60">Last Updated: {lastUpdated}</p>
                    </div>

                    <div className="prose prose-invert max-w-none space-y-8">
                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">1. Introduction</h2>
                            <p className="text-white/80 leading-relaxed">
                                Shiro Security, LLC ("Shiro Security," "we," "us," or "our") is committed to protecting the privacy and security of your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our autonomous AI-powered cybersecurity platform and related services (collectively, the "Services").
                            </p>
                            <p className="text-white/80 leading-relaxed mt-4">
                                By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, please do not use our Services.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">2. Information We Collect</h2>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">2.1 Information You Provide</h3>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li>Account registration information (name, email, company name, password)</li>
                                <li>Billing and payment information</li>
                                <li>Communications with our support team</li>
                                <li>Configuration settings and preferences</li>
                            </ul>

                            <h3 className="text-xl font-semibold mb-3 text-white">2.2 Information Collected Automatically</h3>
                            <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                                <li>Security logs and telemetry data from protected devices</li>
                                <li>Network traffic metadata (IP addresses, timestamps, connection data)</li>
                                <li>Threat detection and response data</li>
                                <li>Device information (operating system, hardware identifiers)</li>
                                <li>Usage analytics and performance metrics</li>
                            </ul>

                            <h3 className="text-xl font-semibold mb-3 text-white">2.3 Security Monitoring Data</h3>
                            <p className="text-white/80 leading-relaxed">
                                As a cybersecurity service, we necessarily collect and process security-related data from your systems, including but not limited to: system logs, authentication events, file access patterns, network connections, and potential threat indicators. This data is essential for providing our AI-powered threat detection and response services.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">3. How We Use Your Information</h2>
                            <p className="text-white/80 leading-relaxed mb-4">We use the collected information for the following purposes:</p>
                            <ul className="list-disc list-inside text-white/80 space-y-2">
                                <li>To provide, maintain, and improve our cybersecurity Services</li>
                                <li>To detect, investigate, and respond to security threats</li>
                                <li>To train and improve our AI security models (using anonymized/aggregated data)</li>
                                <li>To generate security reports and audit logs</li>
                                <li>To communicate with you about your account and Services</li>
                                <li>To comply with legal obligations and respond to lawful requests</li>
                                <li>To enforce our Terms of Service and protect our rights</li>
                            </ul>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">4. Data Sharing and Disclosure</h2>
                            <p className="text-white/80 leading-relaxed mb-4">We may share your information in the following circumstances:</p>
                            
                            <h3 className="text-xl font-semibold mb-3 text-white">4.1 Service Providers</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                We may share data with third-party service providers who assist in operating our Services (cloud hosting, payment processing, analytics). These providers are contractually bound to protect your data.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">4.2 Legal Requirements</h3>
                            <p className="text-white/80 leading-relaxed mb-4">
                                We may disclose information if required by law, court order, or government request, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
                            </p>

                            <h3 className="text-xl font-semibold mb-3 text-white">4.3 Business Transfers</h3>
                            <p className="text-white/80 leading-relaxed">
                                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">5. Data Security</h2>
                            <p className="text-white/80 leading-relaxed mb-4">
                                We implement industry-standard security measures to protect your data, including:
                            </p>
                            <ul className="list-disc list-inside text-white/80 space-y-2">
                                <li>Encryption of data in transit (TLS 1.3) and at rest (AES-256)</li>
                                <li>Multi-factor authentication for account access</li>
                                <li>Regular security audits and penetration testing</li>
                                <li>Access controls and audit logging</li>
                                <li>Secure data centers with SOC 2 Type II compliance</li>
                            </ul>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">6. Data Retention</h2>
                            <p className="text-white/80 leading-relaxed">
                                We retain your data for as long as your account is active or as needed to provide Services. Security logs and audit trails are retained according to your subscription plan (7 days to 1 year). After account termination, we may retain certain data as required by law or for legitimate business purposes. You may request deletion of your data subject to legal and contractual obligations.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">7. Your Rights</h2>
                            <p className="text-white/80 leading-relaxed mb-4">Depending on your jurisdiction, you may have the following rights:</p>
                            <ul className="list-disc list-inside text-white/80 space-y-2">
                                <li><strong>Access:</strong> Request a copy of your personal data</li>
                                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                                <li><strong>Objection:</strong> Object to certain processing activities</li>
                                <li><strong>Restriction:</strong> Request restriction of processing</li>
                            </ul>
                            <p className="text-white/80 leading-relaxed mt-4">
                                To exercise these rights, contact us at privacy@shirosec.com.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">8. International Data Transfers</h2>
                            <p className="text-white/80 leading-relaxed">
                                Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers, including Standard Contractual Clauses and compliance with applicable data protection frameworks.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">9. Children's Privacy</h2>
                            <p className="text-white/80 leading-relaxed">
                                Our Services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete such information.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">10. Changes to This Policy</h2>
                            <p className="text-white/80 leading-relaxed">
                                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of our Services after such changes constitutes acceptance of the updated policy.
                            </p>
                        </section>

                        <section className="glass-card rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-4 text-cyan-400">11. Contact Us</h2>
                            <p className="text-white/80 leading-relaxed">
                                If you have questions about this Privacy Policy or our data practices, please contact us:
                            </p>
                            <div className="mt-4 text-white/80">
                                <p><strong>Shiro Security, LLC</strong></p>
                                <p>Email: privacy@shirosec.com</p>
                                <p>Website: www.shirosec.com</p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-white/5">
                <div className="max-w-7xl mx-auto text-center text-white/40 text-sm">
                    &copy; {new Date().getFullYear()} Shiro Security, LLC. All rights reserved.
                </div>
            </footer>
        </div>
    );
};

export default Privacy;
